import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Table as KSNMTable } from 'ksnm-common-ui/lib/Table2';
import { getLocalRowsByCheckBoxStatus } from 'ksnm-common-ui/lib/Table2/utils/getLocalRowsByCheckBoxStatus';
import { FILTER } from 'ksnm-common-ui/lib/Table2/utils/TableRequest/TableRequest.interface';

import { RoutePath } from '@components/AppRouter/constants';
import { getExcelWithAudit } from '@src/utils/getExcelWithAudit';
import { useTableTitleWithCount } from '@src/utils/hooks/useTableTitleWithCount';
import { useUpdateTable } from '@src/utils/hooks/useUpdateTable';
import { objectFieldsToSnakeCase } from '@src/utils/objectFieldsToCamelCase';
import { constantStore } from '@stores/constantStore';
import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';
import { reportPeriodsStore } from '@stores/dictionaryStores/reportPeriodsStore';
import { ksnmTableStore } from '@stores/ksnmTableStore/ksnmTableStore';

import { ActionButton } from './ActionButton';
import { columns } from './columns';

import styles from './Table.module.scss';

const { TABLE_NAMES } = constantStore;

export const Table = observer(() => {
  const { breadcrumbs, selectNode, reportPeriods, setChecked } = reportPeriodsStore;

  const { push } = useHistory();

  const [key, update, externalControl] = useUpdateTable({ tableName: TABLE_NAMES.DICTIONARY_REPORT_PERIODS });

  useEffect(() => {
    update();
  }, [selectNode, update]);

  useEffect(() => {
    fetchReferences(['reportGroups']);
  }, []);

  const hardFilter = useMemo(() => {
    if (selectNode) {
      return [
        [
          {
            name: 'report_group_code',
            type: FILTER.EQ,
            values: [selectNode.reportGroupCode ?? ''],
            filterIsInverted: false,
          },
        ],
      ];
    }

    return undefined;
  }, [selectNode]);

  const localData = useMemo(() => reportPeriods.map((item) => objectFieldsToSnakeCase(item)), [reportPeriods]);

  const onCheckBoxChange = useCallback(
    async (tr, sr) => {
      // TODO переделать когда, в таблицном компоненте на беке добавят запрос для получение выбранных строк
      const rows = await getLocalRowsByCheckBoxStatus({
        tableRequest: { ...tr, hardFilter },
        localData: {
          data: localData,
          fieldFormat: {},
        },
        selectRows: sr,
        keyField: 'period_code',
      });

      setChecked(rows);
    },
    [hardFilter, localData, setChecked],
  );

  const onRowClick = useCallback(
    (value) => {
      push(`${RoutePath.DICTIONARY_REPORT_PERIODS}/${value?.report_group_code}/${value?.period_code}`);
    },
    [push],
  );

  const title = useMemo(
    () => (
      <div className={styles.breadcrumbs}>
        {breadcrumbs.map((value) => (
          <div key={value}>{value}</div>
        ))}
      </div>
    ),
    [breadcrumbs],
  );

  const [titleWithCount, onTableChange] = useTableTitleWithCount(title);

  if (!selectNode) return null;

  return (
    <div className={styles.table}>
      <KSNMTable
        externalControl={externalControl}
        tableName={TABLE_NAMES.DICTIONARY_REPORT_PERIODS}
        columns={columns}
        tableTitle={titleWithCount}
        resource="v_report_period_tc"
        externalFetch={ksnmTableStore.externalFetch}
        externalFetchXLSX={getExcelWithAudit('Справочник "Календарь отчетных форм"')}
        hardFilter={hardFilter}
        onRowClick={onRowClick}
        onCheckBoxChange={onCheckBoxChange}
        extendTableButtons={<ActionButton update={update} />}
        showAggregateRow={false}
        key={key}
        keyField="period_code"
        onTableChange={onTableChange}
      />
    </div>
  );
});
